import { useOptionalField } from '@app/form';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';

const useStyles = createUseStyles((theme: Theme) => ({
  altcha: {
    ...theme.fonts.caption,
    '& .altcha-main': {
      display: 'flex',
      gap: theme.marginBase,
      alignItems: 'flex-end',
    },
    '& input[type="checkbox"]': {
      '-webkit-appearance': 'none',
      appearance: 'none',
      backgroundColor: theme.colors.white,
      margin: 0,
    },
    '& input[type="checkbox"]::before': {
      fontFamily: 'remixicon',
      fontStyle: 'normal',
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',

      fontSize: theme.iconSize.regular,
      '&:hover': {
        color: theme.colors.blueAccessible,
      },

      content: '"\\eb7f"',
    },
    '& input[type="checkbox"]:checked::before': {
      content: '"\\eb85"',
      color: theme.colors.blueAccessible,
    },
  },
}));

interface Props {
  className?: string;
  invisible?: boolean;
  name?: string;
  onLoadingStateChange?: (isLoading: boolean) => any;
}

export interface CaptchaHandle {
  /** Runs a fresh verification and resolves with its payload, for use outside of a Formik form */
  getPayload: () => Promise<string>;
}

export const Captcha = forwardRef<CaptchaHandle, Props>(function Captcha(
  { className, invisible, name = 'altcha', onLoadingStateChange },
  ref,
) {
  const theme = useTheme<Theme>();
  const classes = useStyles();
  const widgetRef = useRef<AltchaWidget & AltchaWidgetMethods & HTMLElement>(
    null,
  );
  const [visibleState, setVisible] = useState(false);
  const visible = visibleState || !invisible;

  const { helpers } = useOptionalField({ name });
  const formik = useFormikContext();
  const setValue = helpers?.setValue;
  const setSubmitting = formik?.setSubmitting;
  const pendingRef = useRef<{
    resolve: (payload: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  // reset() emits an 'unverified' state that is not a failed verification
  const isResettingRef = useRef(false);

  useImperativeHandle(
    ref,
    () => ({
      getPayload: () => {
        const widget = widgetRef.current;
        if (!widget) {
          return Promise.reject(new Error('Captcha is not ready'));
        }
        pendingRef.current?.reject(new Error('Captcha verification restarted'));
        return new Promise<string>((resolve, reject) => {
          pendingRef.current = { resolve, reject };
          isResettingRef.current = true;
          widget.reset();
          widget.verify();
        });
      },
    }),
    [],
  );

  useEffect(() => {
    const { current } = widgetRef;
    if (!current) {
      return;
    }

    const handleStateChange = (event: Event | CustomEvent) => {
      if (!('detail' in event)) {
        return;
      }

      onLoadingStateChange?.(event.detail.state === 'verifying');
      setSubmitting?.(true);
      if (setValue) {
        setValue(event.detail.payload);
      }

      if (event.detail.state === 'verified' && event.detail.payload) {
        pendingRef.current?.resolve(event.detail.payload);
        pendingRef.current = null;
      }

      if (event.detail.state === 'error') {
        pendingRef.current?.reject(new Error('Captcha verification failed'));
        pendingRef.current = null;
      }

      if (event.detail.state === 'code') {
        // If a code challenge is requested, show the widget
        setVisible(true);
      }

      if (event.detail.state === 'unverified') {
        if (isResettingRef.current) {
          isResettingRef.current = false;
        } else {
          // Verification failed, show the widget or alert the user
          setVisible(true);
        }
      }
    };

    current.addEventListener('statechange', handleStateChange);
    return () => current.removeEventListener('statechange', handleStateChange);
  }, [
    onLoadingStateChange,
    name,
    widgetRef,
    setVisible,
    setValue,
    setSubmitting,
  ]);

  return (
    <div className={classNames(classes.altcha, className)}>
      <altcha-widget
        ref={widgetRef}
        challengeurl="https://ats.bilendi.com/v1/challenge?apiKey=key_1jbkn887v0q40n5dhns"
        hidelogo
        hidefooter
        auto={invisible ? 'onsubmit' : undefined}
        style={{
          '--altcha-border-width': '1px',
          '--altcha-border-radius': `${theme.borderRadius.large}px`,
          '--altcha-color-base': theme.colors.white,
          '--altcha-color-border': theme.colors.grayBorder,
          '--altcha-color-text': theme.colors.inkPrimary,
          '--altcha-color-border-focus': theme.colors.blue,
          '--altcha-color-error-text': theme.colors.redAccessible,
          '--altcha-color-footer-bg': theme.colors.grayBackground,
          '--altcha-max-width': 'none',
          // @ts-ignore
          '--altcha-color-active': theme.colors.blueAccessible,
          display: visible ? 'block' : 'none',
        }}
      ></altcha-widget>
    </div>
  );
});
